import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence } from "framer-motion";
import { ListChecks, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import TripBuilder from "@/components/owner/TripBuilder";
import { AgentSheet } from "@/components/owner/AgentSheet";

export default function TripAdminPage() {
  const { adminToken } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(() => {
    axios
      .get(`${API}/trip-admin/${adminToken}`)
      .then((res) => setTrip(res.data))
      .catch(() => setError(true));
  }, [adminToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div data-testid="trip-admin-not-found" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-300">This admin link is not valid.</p>
        <p className="mt-3 text-sm text-neutral-500">Ask the owner to send you a fresh invitation.</p>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070709]">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div data-testid="trip-admin-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <header className="border-b border-amber-500/15 bg-[#0c0c10]/90">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-4">
          <img src="/assets/logo.png" alt="" className="h-10 w-auto" />
          <div className="flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
              Trip Admin — this journey only
            </p>
            <h1 className="font-serif text-xl font-light text-neutral-50">{trip.client.name}</h1>
          </div>
          <button
            data-testid="admin-sheet-button"
            onClick={() => setSheetOpen(true)}
            className="flex items-center gap-2 border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
          >
            <ListChecks className="h-4 w-4" /> Agent Sheet
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <TripBuilder trip={trip} adminToken={adminToken} onDone={load} onCancel={load} />
      </main>
      <AnimatePresence>
        {sheetOpen && (
          <AgentSheet
            trip={trip}
            opsUrl={`${API}/trip-admin/${adminToken}/ops`}
            pdfUrl={`${API}/trip-admin/${adminToken}/agent-sheet.pdf`}
            onClose={() => {
              setSheetOpen(false);
              load();
            }}
            onChange={(patch) => setTrip((t) => ({ ...t, ...patch }))}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
